// sort-imports-ignore
// Side effect first: strips a password setup token from the URL before
// anything else (router, GTM, Sentry) can read it.
import './utils/auth/captureSetupTokenOnLoad';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { onCLS, onINP, onLCP } from 'web-vitals';
import { Metric } from 'web-vitals/src/types/base';
import App from './App';
import Auth0ProviderWithRedirect from './components/auth/Auth0ProviderWithRedirect';
import CognitoManagedLoginProvider from './components/auth/cognito/CognitoManagedLoginProvider';
import { configuration } from './configuration';
import './index.css';
import stateStore from './stateStore';
import { pushDataLayerEvent } from './utils/analytics/dataLayer';
import { initGtm } from './utils/analytics/gtm';
import { initSentry } from './utils/monitoring/initSentry';

initGtm(configuration.GTM_CONTAINER_ID);
initSentry();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

const AuthProvider = !configuration.COGNITO_ENABLED
  ? Auth0ProviderWithRedirect
  : CognitoManagedLoginProvider;

root.render(
  <Provider store={stateStore}>
    <AuthProvider>
      <App />
    </AuthProvider>
  </Provider>,
);

// Send web vitals results to GTM's dataLayer as a single `web_vitals` event
// carrying `metric_name`, rather than one event per metric (`CLS`/`INP`/
// `LCP`) as before -- three separate event names for one concept fragments
// GTM triggers built against "a web vital came in" (APP-2232, events.ts).
// See: https://github.com/GoogleChrome/web-vitals#send-the-results-to-google-analytics
function sendToDataLayer({ delta, id, name, value }: Metric) {
  pushDataLayerEvent({
    event: 'web_vitals',
    metric_delta: delta,
    metric_id: id,
    // `Metric['name']` covers every web-vitals metric; only onCLS/onINP/onLCP
    // are wired up below, so it is always one of these three at runtime.
    metric_name: name as 'CLS' | 'INP' | 'LCP',
    metric_value: value,
    value: delta,
    // OPTIONAL: any additional params or debug info here.
    // See: https://web.dev/articles/debug-performance-in-the-field
  });
}

onCLS(sendToDataLayer);
onINP(sendToDataLayer);
onLCP(sendToDataLayer);
